import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ProductDetailPage } from '~/features/catalog-operations/pages/product-detail-page';
import {
  MEMBERSHIP_CATEGORIES,
  MEMBERSHIP_GROUPS,
} from '~/features/catalog-operations/utils/insight-membership';
import { RequireChannelConnection } from '~/features/onboarding/components/require-channel-connection';

/** Detail route is non-layout (`products_/`) so the page renders its own
 *  header/breadcrumb instead of inheriting the listing's tabs.
 *  - `tab` is forwarded from the listing so the back-breadcrumb returns to
 *    the same tab the user was on.
 *  - `from='needs-attention'` flags entries from the cross-batch list.
 *  - `from='batch-detail'` flags entries from the batch page's insight drawer;
 *    `returnTab` + `membership*` let the breadcrumb re-open that drawer. */
const searchSchema = z.object({
  tab: z.enum(['newly-enriched', 'needs-attention']).optional(),
  from: z.enum(['needs-attention', 'batch-detail']).optional(),
  returnTab: z
    .enum([
      'delivery-health',
      'performance-signals',
      'enrichment-coverage',
      'optimization-quality',
      'run-details',
    ])
    .optional()
    .catch(undefined),
  membership: z.enum(MEMBERSHIP_CATEGORIES).optional().catch(undefined),
  membershipLabel: z.string().optional().catch(undefined),
  membershipGroup: z.enum(MEMBERSHIP_GROUPS).optional().catch(undefined),
  detailTab: z
    .enum([
      'product-detail',
      'gap-analysis',
      'search-descriptors',
      'search-impact',
      'languages',
      'version-history',
    ])
    .optional(),
});

export const Route = createFileRoute(
  '/_authenticated/_tenant/catalog-operations/$batchId_/products_/$productId',
)({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { batchId, productId } = Route.useParams();
  const { tab, from, detailTab, returnTab, membership, membershipLabel, membershipGroup } =
    Route.useSearch();
  return (
    <RequireChannelConnection>
      <ProductDetailPage
        batchId={batchId}
        productId={productId}
        tab={tab}
        from={from}
        {...(detailTab !== undefined ? { detailTab } : {})}
        {...(from === 'batch-detail'
          ? {
              batchReturn: {
                ...(returnTab ? { tab: returnTab } : {}),
                ...(membership && membershipLabel
                  ? {
                      membership: {
                        category: membership,
                        label: membershipLabel,
                        group: membershipGroup,
                      },
                    }
                  : {}),
              },
            }
          : {})}
      />
    </RequireChannelConnection>
  );
}
